import React, { useCallback, useEffect, useRef, useState } from "react";
import styles from './index.module.css';
import { useDispatch, useSelector } from "react-redux";
import { showToast } from "../../../components/ToastMsg";
import BeatLoader from "react-spinners/BeatLoader";
import { getAllCustomers } from "../../../redux/features/customers/customersService";
import Dropdown from "../../../components/Dropdown/Dropdown";
import { getAllStaffMembers } from "../../../redux/features/staff/staffService";
import { editOrder, getAllOrders } from "../../../redux/features/orders/ordersService";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCircleCheck, faPlusCircle } from "@fortawesome/free-solid-svg-icons";
import { useNavigate } from "react-router-dom";
import Pagination from "../../../components/Pagination";
import TableSkeleton from "../../../components/Skeleton";
import NoResultsFound from "../../../components/NoResultsFound";
import Modal from "../../../components/Modal";
import { NumericFormat } from "react-number-format";

const Order = () => {

    const dispatch = useDispatch();
    const navigate = useNavigate();
    const customerDropdownRef = useRef(null);
    const agentDropdownRef = useRef(null);

    const { customers } = useSelector((state) => state.customer.allCustomers);
    const { isOrdersLoading, orders, pageCount } = useSelector((state) => state.order.allOrders);
    const { staffMembers } = useSelector((state) => state.staff.allStaffMembers);

    const [formData, setFormData] = useState({});

    const [errorMessages, setErrorMessages] = useState({});
    const [skip, setSkip] = useState(0);
    const [selectedCustomer, setSelectedCustomer] = useState({
        id: '',
        name: ''
    });
    const [selectedAgent, setSelectedAgent] = useState({
        id: '',
        name: ''
    });
    const [isCancelModalOpen, setCancelModalOpen] = useState(false);
    const [isConfirmModalOpen, setConfirmModalOpen] = useState(false);
    const [isShipModalOpen, setShipModalOpen] = useState(false);
    const [selectedOrder, setSelectedOrder] = useState(null);

    const fetchAllOrders = useCallback(() => {
        dispatch(getAllOrders({
            customer_id: selectedCustomer?.id,
            agent_id: selectedAgent?.id,
            skip: skip,
            limit: 25
        })).unwrap();
    }, [dispatch, selectedCustomer, selectedAgent, skip]);

    const fetchActiveCustomers = useCallback(() => {
        dispatch(getAllCustomers({
            status: 'Active',
        })).unwrap();
    }, [dispatch]);

    const fetchActiveCustomerSpecialists = useCallback(() => {
        dispatch(getAllStaffMembers({
            status: 'Active',
            role_name: 'customer-specialist'
        })).unwrap();
    }, [dispatch]);


    useEffect(() => {
        fetchAllOrders();
        fetchActiveCustomers();
        fetchActiveCustomerSpecialists();
    }, [fetchAllOrders]);

    const openNewOrder = () => navigate('/admin/orders/create-order');
    const viewOrder = (order) => navigate(`/admin/orders/${order.id}`);

    const handleSelect = (option) => {
        setFormData({ ...formData, customer_id: option.id });
        setErrorMessages({ ...errorMessages, name: "" });
        setSelectedCustomer({
            id: option.id,
            name: option.full_name
        });
    };

    const handleSelectAgent = (option) => {
        setFormData({ ...formData, agent_id: option.id });
        setErrorMessages({ ...errorMessages, agent: "" });
        setSelectedAgent({
            id: option.id,
            name: option.full_name
        });
    };

    const handlePageChange = (pageNumber) => {
        setSkip(pageNumber - 1);
    };

    const handleCancelYes = async () => {
        setCancelModalOpen(false);
        try {
            const data = {
                id: +selectedOrder.id,
                status: "Order Cancelled"
            }
            const response = await dispatch(editOrder(data)).unwrap();
            showToast('success', response.message);
            setSelectedOrder(null);
            fetchAllOrders();
        }
        catch (error) {
            setSelectedOrder(null);
            showToast('error', error.message);
        };
    }

    const closeCancelModal = () => setCancelModalOpen(false);

    const handleConfirmYes = async () => {
        setConfirmModalOpen(false);
        try {
            const data = {
                id: +selectedOrder.id,
                status: "Order Confirmed"
            }
            const response = await dispatch(editOrder(data)).unwrap();
            showToast('success', response.message);
            fetchAllOrders();
        }
        catch (error) {
            showToast('error', error.message);
        };
    }

    const closeConfirmModal = () => setConfirmModalOpen(false);

    const handleShipYes = async () => {
        setShipModalOpen(false);
        try {
            const data = {
                id: +selectedOrder.id,
                status: "Order Shipped"
            }
            const response = await dispatch(editOrder(data)).unwrap();
            showToast('success', response.message);
            fetchAllOrders();
        }
        catch (error) {
            showToast('error', error.message);
        };
    }

    const closeShipModal = () => setShipModalOpen(false);

    return (
        <div className={`card-section`}>
            <div className={`search-card`}>
                <div className={`search-card-header`}>
                    <h1 className={`form-title`}>Orders <FontAwesomeIcon icon={faPlusCircle} onClick={openNewOrder} style={{ cursor: 'pointer' }} /></h1>
                </div>
                <div className={`search-card-body`}>
                    <div className={`form-input-row ${styles.filterRow}`}>
                        <div className={`form-input-group`}>
                            <label htmlFor='full_name' className={`form-label`}>
                                Customer Name
                            </label>
                            <Dropdown
                                ref={customerDropdownRef}
                                options={customers.map(customer => ({
                                    ...customer,
                                    name: customer.full_name
                                }))}
                                placeholder="Choose an Option"
                                onSelect={handleSelect}
                                value={selectedCustomer.name}
                            />
                        </div>
                        <div className={`form-input-group`}>
                            <label htmlFor='full_name' className={`form-label`}>
                                Customer Specialist
                            </label>
                            <Dropdown
                                ref={agentDropdownRef}
                                options={staffMembers.map(member => ({
                                    ...member,
                                    name: member.full_name
                                }))}
                                placeholder="Choose an Option"
                                onSelect={handleSelectAgent}
                                value={selectedAgent.name}
                            />
                        </div>
                    </div>
                    {isOrdersLoading ?
                        <TableSkeleton columns={["Customer Code", "Name", "Phone Number", "Agent", "Status", "Total", "Action"]} rowCount={10} height={20} />
                        : orders.length > 0 ?
                            <div className={`table-container`}>
                                <table className={`table`}>
                                    <thead className={`thead`}>
                                        <tr>
                                            <th className={`theadColumnOne`}>Customer Code</th>
                                            <th>Name</th>
                                            <th>Phone Number</th>
                                            <th>Agent</th>
                                            <th>Status</th>
                                            <th>Total</th>
                                            <th>Action</th>
                                        </tr>
                                    </thead>
                                    <tbody className={`tbody`}>
                                        {orders?.map((order) => (
                                            <tr key={order.id}>
                                                <td className={`tdataColumnOne`}>
                                                    {order.customer.customer_code}
                                                </td>
                                                <td>{order.customer.full_name}</td>
                                                <td>{order.customer.phone_number}</td>
                                                <td>{order.agent.full_name}</td>
                                                <td>{order.status}</td>
                                                <td>
                                                    <NumericFormat
                                                        value={order?.total_amount}
                                                        displayType="text"
                                                        thousandSeparator=","
                                                        decimalSeparator="."
                                                        decimalScale={2}
                                                        fixedDecimalScale={true}
                                                    />
                                                </td>
                                                <td className={`actionsBtn ${styles.actionCol}`}>
                                                    {(order?.status !== 'Order Shipped' && order?.status !== 'Order Completed' && order?.status !== 'Order Cancelled') &&
                                                        <button className={`btn-edit-label`} onClick={() => {
                                                            viewOrder(order)
                                                        }}>View</button>
                                                    }
                                                    {(order?.status === 'Order Created') &&
                                                        <button className={`btn-edit-label`} onClick={() => {
                                                            setSelectedOrder(order);
                                                            setConfirmModalOpen(true);
                                                        }}>Confirm Order</button>
                                                    }
                                                    {(order?.status === 'Order Confirmed') &&
                                                        <button className={`btn-edit-label`} onClick={() => {
                                                            setSelectedOrder(order);
                                                            setShipModalOpen(true);
                                                        }}>Ship Order</button>
                                                    }
                                                    {(order?.status !== 'Order Shipped' && order?.status !== 'Order Completed' && order?.status !== 'Order Cancelled') &&
                                                        <button className={`btn-edit-label`}
                                                            onClick={() => {
                                                                setSelectedOrder(order);
                                                                setCancelModalOpen(true);
                                                            }}>Cancel Order</button>
                                                    }
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                            :
                            <NoResultsFound />
                    }
                </div>
                <div className={`search-card-footer`}>
                    {pageCount > 0 && (
                        <div className={styles.paginationContainer}>
                            <Pagination
                                currentPage={skip + 1}
                                totalPages={pageCount}
                                onPageChange={handlePageChange}
                            />
                        </div>
                    )}
                </div>
            </div>
            {isCancelModalOpen && (
                <Modal
                    title='Cancel Order'
                    onClose={closeCancelModal}
                    onYes={handleCancelYes}
                    isInput={false}
                    iconName={faCircleCheck}
                    bodyText={`Are you sure you want to cancel the Order?`}
                    colorType={`var(--green)`}
                />
            )}
            {isConfirmModalOpen && (
                <Modal
                    title='Confirm Order'
                    onClose={closeConfirmModal}
                    onYes={handleConfirmYes}
                    isInput={false}
                    iconName={faCircleCheck}
                    bodyText={`Are you sure you want to confirm the Order?`}
                    colorType={`var(--green)`}
                />
            )}
            {isShipModalOpen && (
                <Modal
                    title='Ship Order'
                    onClose={closeShipModal}
                    onYes={handleShipYes}
                    isInput={false}
                    iconName={faCircleCheck}
                    bodyText={`Are you sure you want to ship the Order?`}
                    colorType={`var(--green)`}
                />
            )}
        </div>
    );
};

export default Order;
